import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Loader2, RefreshCw, FileSearch, Stethoscope, ArrowRight,
  CheckCircle2, AlertCircle, MoreVertical, AlertTriangle,
} from "lucide-react";

/**
 * TesterPrimaryActionCard — iter182 unification of the tester MOT-day
 * primary action surface.
 *
 * Replaces the previous "three competing buttons" UX (Check DVSA,
 * Import External, Resolve Manually) with ONE state-aware primary
 * button reflecting the next step the tester needs to take.
 *
 * State machine:
 *   no_outcome_no_baseline → "Pull MOT Result from DVSA"
 *   no_outcome_with_baseline → "Import latest MOT result"
 *   outcome_mcc_pending → "Complete MCC (n cards)"  (opens external modal)
 *   outcome_mcc_complete → "Submit to FOH"
 *   submitted → "View on FOH dashboard →"
 *
 * Secondary actions ("Resolve manually", "Schedule slot", etc.) live in
 * a kebab menu so they never compete with the primary path.
 *
 * Props:
 *   sessionId         — intake session id (required)
 *   jobId             — job id, for the post-submit FOH-dashboard link
 *   outcomeId         — current outcome id (null if no outcome yet)
 *   mccEntries        — array of mcc_entry dicts (level, status, …)
 *   submittedAt       — outcome.mcc_released_at iso string (null if not yet)
 *   onAction          — callback after any state change; parent re-fetches
 *   onOpenMcc         — () => void — opens the MCC capture modal upstream
 *   onOpenAdvanced    — () => void — toggles the "More options" panel upstream
 */
export default function TesterPrimaryActionCard({
  sessionId,
  jobId,
  outcomeId,
  mccEntries = [],
  submittedAt = null,
  onAction,
  onOpenMcc,
  onOpenAdvanced,
}) {
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const loadWorkflow = async () => {
    try {
      const r = await axios.get(`${API}/intake/sessions/${sessionId}/mot-workflow`);
      setWorkflow(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load MOT workflow");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) loadWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Derive primary state from outcome + MCC + workflow snapshot.
  const state = useMemo(() => {
    if (submittedAt) return "submitted";
    if (outcomeId) {
      const blocking = (mccEntries || []).filter(
        (e) => Number(e.level || 0) >= 3
          && !["complete", "skipped", "escalated"].includes(e.status),
      );
      if (blocking.length > 0) return "outcome_mcc_pending";
      return "outcome_mcc_complete";
    }
    if (workflow?.baseline?.test_number) return "no_outcome_with_baseline";
    return "no_outcome_no_baseline";
  }, [outcomeId, mccEntries, submittedAt, workflow]);

  // ---- action handlers ----------------------------------------------------

  const pullFromDvsa = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-workflow/check-dvsa`,
      );
      const outcome = r.data?.result?.outcome;
      const msg = r.data?.result?.message;
      setInfo(outcome === "imported" ? `Result imported — ${msg}` : msg);
      await loadWorkflow();
      if (outcome === "imported" && onAction) onAction("imported");
    } catch (err) {
      setError(err?.response?.data?.detail || "DVSA check failed.");
    } finally {
      setBusy(false);
    }
  };

  const importBaseline = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await axios.post(
        `${API}/intake/sessions/${sessionId}/mot-workflow/import-baseline-as-outcome`,
        { reason: "Tester import — primary action" },
      );
      const outcome = r.data?.result?.outcome;
      setInfo(
        outcome === "already_imported"
          ? "MOT result already imported — refreshing."
          : `Imported — ${r.data?.result?.message || "ready for MCC."}`,
      );
      await loadWorkflow();
      if (onAction) onAction("imported");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not import MOT result.");
    } finally {
      setBusy(false);
    }
  };

  const submitToFoh = async () => {
    if (!outcomeId) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await axios.post(`${API}/mcc/outcomes/${outcomeId}/ready-for-foh`);
      setInfo("Submitted — job now on FOH dashboard in red.");
      if (onAction) onAction("submitted");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === "object" ? detail?.message : detail)
        || "Submit blocked — review MCC cards.",
      );
    } finally {
      setBusy(false);
    }
  };

  // ---- presentation -------------------------------------------------------

  if (!sessionId) return null;
  if (loading) {
    return (
      <section
        className="rounded-xl border border-slate-200 bg-white px-5 py-4"
        data-testid="tester-primary-action-loading"
      >
        <Loader2 className="inline w-4 h-4 animate-spin mr-2 text-slate-500" />
        <span className="text-[14px] text-slate-500">Loading MOT workflow…</span>
      </section>
    );
  }

  const pendingMcc = (mccEntries || []).filter(
    (e) => Number(e.level || 0) >= 3
      && !["complete", "skipped", "escalated"].includes(e.status),
  ).length;

  const cfg = (() => {
    switch (state) {
      case "no_outcome_no_baseline":
        return {
          headline: "Ready to pull MOT result",
          body: "Tap below — Yestimate fetches the latest DVSA test on file and prepares the MCC cards.",
          cta: "Pull MOT Result from DVSA",
          icon: <RefreshCw className="w-4 h-4 mr-2" />,
          onClick: pullFromDvsa,
          tone: "teal",
        };
      case "no_outcome_with_baseline":
        return {
          headline: "DVSA has a recent MOT on file",
          body: `Test #${workflow?.baseline?.test_number} (${workflow?.baseline?.test_result || "—"}). One tap imports it and prepares MCC cards.`,
          cta: "Import latest MOT result",
          icon: <FileSearch className="w-4 h-4 mr-2" />,
          onClick: importBaseline,
          tone: "sky",
        };
      case "outcome_mcc_pending":
        return {
          headline: `${pendingMcc} MCC card${pendingMcc === 1 ? "" : "s"} to complete`,
          body: "Level 3+ defects need technical context before FOH can price. 20–45 seconds per card.",
          cta: `Complete MCC (${pendingMcc} card${pendingMcc === 1 ? "" : "s"}) →`,
          icon: <Stethoscope className="w-4 h-4 mr-2" />,
          onClick: () => onOpenMcc && onOpenMcc(),
          tone: "amber",
        };
      case "outcome_mcc_complete":
        return {
          headline: "MCC complete — ready to submit",
          body: "Submitting hands this job to FOH for pricing. FOH will see it in red on their dashboard.",
          cta: "Submit to FOH",
          icon: <ArrowRight className="w-4 h-4 mr-2" />,
          onClick: submitToFoh,
          tone: "emerald",
        };
      case "submitted":
        return {
          headline: "Submitted to FOH",
          body: "MCC released. The job now sits in red on the FOH dashboard awaiting pricing.",
          cta: "View on FOH Dashboard →",
          icon: <CheckCircle2 className="w-4 h-4 mr-2" />,
          onClick: () => window.open(`/app/foh/job/${jobId}`, "_self"),
          tone: "emerald",
          ctaVariant: "outline",
        };
      default:
        return null;
    }
  })();

  if (!cfg) return null;

  const tonePalette = {
    teal:    { ring: "ring-teal-300",    bg: "bg-teal-50/60",    btn: "bg-teal-700 hover:bg-teal-800 text-white" },
    sky:     { ring: "ring-sky-300",     bg: "bg-sky-50/60",     btn: "bg-sky-700 hover:bg-sky-800 text-white" },
    amber:   { ring: "ring-amber-300",   bg: "bg-amber-50/70",   btn: "bg-amber-600 hover:bg-amber-700 text-white" },
    emerald: { ring: "ring-emerald-300", bg: "bg-emerald-50/70", btn: "bg-emerald-700 hover:bg-emerald-800 text-white" },
  }[cfg.tone] || {};

  return (
    <section
      className={`rounded-xl border-2 border-slate-200 ${tonePalette.bg} px-5 py-4 ring-1 ${tonePalette.ring}`}
      data-testid="tester-primary-action"
      data-state={state}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">
            MOT — your next action
          </p>
          <h2
            className="text-[19px] font-bold text-slate-900 leading-snug"
            data-testid="tester-primary-action-headline"
          >
            {cfg.headline}
          </h2>
          <p className="text-[14px] text-slate-700 mt-1 leading-snug">
            {cfg.body}
          </p>
        </div>
        {onOpenAdvanced && (
          <button
            type="button"
            onClick={onOpenAdvanced}
            className="text-slate-500 hover:text-slate-800 p-1.5 rounded hover:bg-slate-100 shrink-0"
            data-testid="tester-primary-more-options"
            title="More options (manual resolve, manual capture, slot…)"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        )}
      </div>

      <Button
        onClick={cfg.onClick}
        disabled={busy}
        size="lg"
        variant={cfg.ctaVariant || "default"}
        className={`w-full h-12 text-[16px] font-semibold ${cfg.ctaVariant === "outline" ? "" : tonePalette.btn}`}
        data-testid="tester-primary-action-btn"
      >
        {busy ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</>
        ) : (
          <>{cfg.icon}{cfg.cta}</>
        )}
      </Button>

      {error && (
        <div
          className="mt-3 text-[13px] text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2"
          data-testid="tester-primary-action-error"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {info && !error && (
        <div
          className="mt-3 text-[13px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 flex items-start gap-2"
          data-testid="tester-primary-action-info"
        >
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>{info}</span>
        </div>
      )}

      {/* Soft note for any Level 1-2 MCC cards still pending — not blocking,
          just visible so the tester knows they could add extra context.
          Made interactive (iter197) so testers can re-open the MCC modal
          to add context to advisory cards or revisit propagated tyre
          sizes without having to escalate a defect first. */}
      {state === "outcome_mcc_complete"
        && (mccEntries || []).some((e) => Number(e.level || 0) <= 2 && e.status === "pending") && (
        <button
          type="button"
          onClick={() => onOpenMcc?.()}
          className="mt-2 text-[12px] italic text-slate-600 hover:text-teal-700 flex items-center gap-1.5 underline-offset-2 hover:underline"
          data-testid="tester-primary-action-l12-hint"
        >
          <AlertTriangle className="w-3 h-3 text-slate-400" />
          Optional: a few Level 1–2 MCC cards are still pending — tap to review.
        </button>
      )}
    </section>
  );
}
